import type {
  ActionGroupFragment,
  BoxFragment,
  ImageBlockFragment,
  ImageBlockGroupFragment,
} from '@seek/cmsu-cms-connect';
import { Box, Stack, useResponsiveValue } from 'braid-design-system';
import React from 'react';

import { getBoxBackgroundProps, getBoxShadowProps } from '../../shared/helper';
import { Asset, AssetType } from '../Asset/Asset';
import { Heading } from '../Heading/Heading';
import { Paragraph } from '../Paragraph/Paragraph';

import { getAccentBarStyle } from './AccentBar';
import { ActionGroup } from '../ActionGroup/ActionGroup';

type Props = Pick<
  ImageBlockFragment,
  | 'heading'
  | 'image'
  | 'paragraph'
  | 'cButtonLink'
  | 'cTextLink'
  | 'dialogButton'
  | 'reverseContent'
  | 'accentBar'
> &
  Pick<
    ImageBlockGroupFragment,
    'imageAlignment' | 'blockBackground' | 'blockBorder'
  > & {
    textAlign?: BoxFragment['textAlign'];
    imageAlignment?: BoxFragment['justifyContent'];
    actions?: Omit<ActionGroupFragment, 'locale'>;
  };

const getAlignment = (textAlign?: BoxFragment['textAlign']) => {
  switch (textAlign) {
    case 'left':
      return 'flexStart';
    case 'right':
      return 'flexEnd';
    default:
      return 'center';
  }
};

export const RegularImageBlock = ({
  image,
  imageAlignment,
  textAlign,
  heading,
  paragraph,
  blockBackground,
  blockBorder,
  actions,
  reverseContent,
  accentBar,
}: Props) => {
  const responsiveValue = useResponsiveValue();
  const isDeskTop = responsiveValue({
    mobile: false,
    desktop: true,
    tablet: false,
    wide: true,
  });
  const isMobile = responsiveValue({
    mobile: true,
    desktop: false,
    tablet: true,
    wide: false,
  });

  const ImageComponent = React.useMemo(
    () =>
      image && (
        <Box
          display="flex"
          justifyContent={imageAlignment ?? 'center'}
          width="full"
          key="image"
        >
          <Box borderRadius="large" overflow="hidden">
            <Asset {...image} assetType={AssetType.DEFAULT_IMAGE} />
          </Box>
        </Box>
      ),
    [image, imageAlignment],
  );

  const ContentComponent = React.useMemo(
    () => (
      <Stack space="medium" key="content" align={textAlign ?? 'center'}>
        {heading && <Heading {...heading} />}
        {paragraph && (
          <Paragraph
            content={paragraph.Paragraph_text?.raw}
            align={paragraph.align}
            tone={paragraph.tone}
            size={paragraph.size}
          />
        )}
        {actions && <Box display="inlineBlock"><ActionGroup {...actions} /></Box>}
      </Stack>
    ),
    [
      heading,
      paragraph,
      actions,
      textAlign
    ],
  );

  const getOrderedContent = () => {
    switch (reverseContent) {
      case 'desktop':
        return isDeskTop
          ? [ContentComponent, ImageComponent]
          : [ImageComponent, ContentComponent];
      case 'both':
        return [ContentComponent, ImageComponent];
      case 'mobile':
        return isMobile
          ? [ContentComponent, ImageComponent]
          : [ImageComponent, ContentComponent];
      default:
        return [ImageComponent, ContentComponent];
    }
  };
  const { background, style: backgroundStyle } =
    getBoxBackgroundProps(blockBackground);
  return (
    <Box
      padding={blockBackground ? 'gutter' : 'none'}
      borderRadius={blockBackground ? 'large' : 'none'}
      height="full"
      justifyContent={getAlignment(textAlign)}
      background={background}
      style={{
        ...backgroundStyle,
        boxShadow: getAccentBarStyle(accentBar, blockBorder),
      }}
      {...getBoxShadowProps(blockBorder)}
    >
      <Stack space="medium" align={textAlign ?? 'center'}>
        {getOrderedContent()}
      </Stack>
    </Box>
  );
};
